import { useCallback, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { useDeviceType } from "@b2bportal/core-utilities";
import { useI18nContext } from "@hopper-b2b/i18n";
import { getEffectiveAmenities } from "../../../hooks/getEffectiveAmenities";
import { HotelsShopSelectors } from "../../../store";

export const useShopPageContainer = ({
  goToNextStep,
}: {
  goToNextStep: () => void;
}) => {
  const { t } = useI18nContext();

  const { isDesktopAndUp } = useDeviceType();

  const {
    data: hotel,
    isLoading,
    isError,
    isUnavailable,
  } = useSelector(HotelsShopSelectors.getSelectedHotelsData);

  const checkInPolicies = useSelector(HotelsShopSelectors.getCheckInPolicies);
  const checkInInformation = useSelector(
    HotelsShopSelectors.getCheckInInformation
  );
  const reviews = useSelector(HotelsShopSelectors.getCustomerReviews);
  const amenities = useSelector(HotelsShopSelectors.getAmenities);
  const hotelMealPlanKind = useSelector(
    HotelsShopSelectors.getHotelMealPlanKind
  );
  const allRoomsHaveFreeBreakfast = useSelector(
    HotelsShopSelectors.getAllRoomsHaveFreeBreakfast
  );
  const roomsLoaded = useSelector(HotelsShopSelectors.isShopRoomsDataAvailable);

  const roomSectionRef = useRef<HTMLDivElement | null>(null);

  const handleChooseRooms = useCallback(() => {
    if (isDesktopAndUp) {
      return roomSectionRef.current
        ? roomSectionRef.current.scrollIntoView({ behavior: "smooth" })
        : null;
    } else {
      goToNextStep();
    }
  }, [goToNextStep, isDesktopAndUp]);

  const effectiveAmenities = useMemo(
    () =>
      getEffectiveAmenities({
        amenities: amenities ?? [],
        hotelMealPlanKind,
        allRoomsHaveFreeBreakfast,
        roomsLoaded,
        t,
      }),
    [amenities, hotelMealPlanKind, allRoomsHaveFreeBreakfast, roomsLoaded, t]
  );

  return useMemo(() => {
    return {
      context: {
        hotel,
        isLoading,
        isUnavailable,
        isError,
        hasLoaded: hotel != null,
        checkInPolicies,
        checkInInformation,
        reviews,
        amenities: effectiveAmenities,
        roomSectionRef,
        isDesktopAndUp,
      },
      handlers: {
        goToNextStep,
        handleChooseRooms,
      },
      text: {
        title: t("core-hotels.shop.title"),
        roomSelectionTitle: t("core-hotels.roomSelection.title"),
        unavailableTitle: t(
          "core-hotels.availabilityPage.results.noResults.hotelUnavailable"
        ),
        unavailableSubtitle: t(
          "core-hotels.availabilityPage.results.noResults.noHotelsAvailable"
        ),
      },
    };
  }, [
    hotel,
    isLoading,
    isError,
    isUnavailable,
    checkInPolicies,
    checkInInformation,
    reviews,
    effectiveAmenities,
    isDesktopAndUp,
    goToNextStep,
    handleChooseRooms,
    t,
  ]);
};
